import { TimelineItem, TimelineTrack } from '../../types';
import { TIMELINE_CONSTANTS } from '../../constants';
import { DragInfoState, DraggedItemSnapshot, GhostInstanceData } from '../../stores/use-timeline-store';
import { snapToGrid, snapToGridAndEdges } from './snapping-utils';
import { validateDropPosition, validateSourceDuration } from './validation-utils';
import { calculateGhostPosition } from './ghost-utils';
import {
  calculateConstrainedDeltaTrack,
  calculateConstrainedDeltaTime,
  createDraggedItemsSnapshot
} from './multi-drag-utils';

const MIN_ITEM_DURATION = 0.1;

interface DragStartParams {
  item: TimelineItem;
  clientX: number;
  clientY: number;
  action: "move" | "resize-start" | "resize-end";
  selectedItemIds: string[];
  tracks: TimelineTrack[];
  setDragInfo: (info: DragInfoState) => void;
  setDraggedItem: (item: TimelineItem) => void;
  setIsDragging: (value: boolean) => void;
  setGhostElement: (ghosts: GhostInstanceData[] | null) => void;
  setIsValidDrop: (value: boolean) => void;
  setInsertionIndex: (index: number | null) => void;
  totalDuration: number;
}

export const handleDragStart = (params: DragStartParams): void => {
  const {
    item,
    clientX,
    clientY,
    action,
    selectedItemIds,
    tracks,
    setDragInfo,
    setDraggedItem,
    setIsDragging,
    setGhostElement,
    setIsValidDrop,
    setInsertionIndex,
    totalDuration
  } = params;

  const itemTrackIndex = tracks.findIndex(track => track.id === item.trackId);
  if (itemTrackIndex === -1) return;

  const itemDuration = item.end - item.start;

  // Create selected items snapshot for multi-drag support
  const selectedItemsSnapshot = createDraggedItemsSnapshot(item.id, selectedItemIds, tracks);

  // Find the primary (clicked) item snapshot
  const primarySnapshot = selectedItemsSnapshot.find(s => s.id === item.id);
  if (!primarySnapshot) return;

  const dragInfo: DragInfoState = {
    id: item.id,
    action,
    startX: clientX,
    startY: clientY,
    startPosition: item.start,
    startDuration: itemDuration,
    startRow: itemTrackIndex,
    isValidDrop: true,
    selectedItemsSnapshot,
  };

  setDragInfo(dragInfo);
  setDraggedItem(item);
  setIsDragging(true);

  // Create initial ghost elements for all dragged items at their original positions
  const initialGhosts = selectedItemsSnapshot.map(snapshot => {
    const snapshotTrackIndex = snapshot.originalRow;
    return calculateGhostPosition(snapshot.originalStart, snapshot.originalDuration, snapshotTrackIndex, totalDuration, tracks.length);
  });

  setGhostElement(initialGhosts);
  setIsValidDrop(true);
  setInsertionIndex(null);
};

interface ResizeCalculationParams {
  action: "resize-start" | "resize-end";
  dragInfo: DragInfoState;
  deltaTime: number;
  tracks: TimelineTrack[];
  excludeIds: string[];
  totalDuration: number;
}

export const calculateResizePosition = (params: ResizeCalculationParams): { start: number; duration: number } => {
  const { action, dragInfo, deltaTime, tracks, excludeIds, totalDuration } = params;
  let newStart: number;
  let newDuration: number;

  if (action === "resize-start") {
    const rawNewStart = dragInfo.startPosition + deltaTime;
    const snappedNewStart = snapToGridAndEdges(rawNewStart, dragInfo.startRow, tracks, excludeIds);
    const originalEnd = dragInfo.startPosition + dragInfo.startDuration;
    newDuration = Math.max(MIN_ITEM_DURATION, originalEnd - snappedNewStart);
    newStart = originalEnd - newDuration;

    // Apply source duration validation for video/audio items
    const primaryItem = dragInfo.selectedItemsSnapshot.find(item => item.id === dragInfo.id);
    if (primaryItem) {
      const validated = validateSourceDuration(
        newStart,
        newDuration,
        originalEnd,
        primaryItem,
        "resize-start"
      );
      newStart = validated.start;
      newDuration = validated.duration;
    }
  } else {
    // resize-end
    newStart = dragInfo.startPosition;
    const rawNewDuration = dragInfo.startDuration + deltaTime;
    const snappedEndPosition = snapToGridAndEdges(
      dragInfo.startPosition + rawNewDuration,
      dragInfo.startRow,
      tracks,
      excludeIds
    );
    newDuration = Math.max(MIN_ITEM_DURATION, snappedEndPosition - newStart);

    // Apply source duration validation for video/audio items
    const primaryItem = dragInfo.selectedItemsSnapshot.find(item => item.id === dragInfo.id);
    if (primaryItem) {
      const validated = validateSourceDuration(
        newStart,
        newDuration,
        0, // Not used for resize-end
        primaryItem,
        "resize-end"
      );
      newDuration = validated.duration;
    }
  }

  // Ensure boundaries
  newStart = Math.max(0, newStart);
  if (newStart + newDuration > totalDuration) {
    newDuration = Math.max(MIN_ITEM_DURATION, totalDuration - newStart);
  }

  return { start: newStart, duration: newDuration };
};

interface MoveCalculationParams {
  dragInfo: DragInfoState;
  deltaTime: number;
  deltaTrack: number;
  tracks: TimelineTrack[];
  excludeIds: string[];
  totalDuration: number;
}

export const calculateMovePosition = (params: MoveCalculationParams): {
  start: number;
  duration: number;
  trackIndex: number;
  constrainedDeltaTrack: number;
} => {
  const { dragInfo, deltaTime, deltaTrack, tracks, excludeIds, totalDuration } = params;

  const isMultiDrag = dragInfo.selectedItemsSnapshot.length > 1;

  // Calculate constrained deltaTrack for multi-drag
  let constrainedDeltaTrack = deltaTrack;
  if (isMultiDrag) {
    constrainedDeltaTrack = calculateConstrainedDeltaTrack(deltaTrack, dragInfo.selectedItemsSnapshot, tracks);
  }

  const effectiveDeltaTrack = isMultiDrag ? constrainedDeltaTrack : deltaTrack;
  const targetTrackIndex = Math.max(0, Math.min(tracks.length - 1, dragInfo.startRow + effectiveDeltaTrack));

  const newStart = snapToGridAndEdges(
    dragInfo.startPosition + deltaTime,
    targetTrackIndex,
    tracks,
    excludeIds
  );
  const newDuration = dragInfo.startDuration;

  // Ensure boundaries
  let finalStart = Math.max(0, newStart);
  let finalDuration = newDuration;

  if (finalStart + finalDuration > totalDuration) {
    finalStart = Math.max(0, totalDuration - finalDuration);
  }

  return {
    start: finalStart,
    duration: finalDuration,
    trackIndex: targetTrackIndex,
    constrainedDeltaTrack
  };
};

interface MultiDragGhostsParams {
  dragInfo: DragInfoState;
  finalStart: number;
  finalDuration: number;
  constrainedDeltaTrack: number;
  tracks: TimelineTrack[];
  totalDuration: number;
  excludeIds: string[];
  getDragInfo: () => DragInfoState | null;
}

export const calculateMultiDragGhosts = (params: MultiDragGhostsParams): {
  ghosts: GhostInstanceData[];
  allValid: boolean;
} => {
  const { dragInfo, finalStart, finalDuration, constrainedDeltaTrack, tracks, totalDuration, excludeIds, getDragInfo } = params;

  const primarySnapshot = dragInfo.selectedItemsSnapshot.find(s => s.id === dragInfo.id);
  if (!primarySnapshot) {
    return { ghosts: [], allValid: false };
  }

  let deltaTime = finalStart - primarySnapshot.originalStart;

  // Calculate constrained deltaTime
  const constrainedDeltaTime = calculateConstrainedDeltaTime(
    deltaTime,
    dragInfo.selectedItemsSnapshot,
    dragInfo.id,
    totalDuration
  );

  deltaTime = constrainedDeltaTime;

  const allGhostElements: GhostInstanceData[] = [];
  let allItemsValid = true;

  // Create ghost elements for all selected items
  for (const snapshot of dragInfo.selectedItemsSnapshot) {
    let itemNewStart: number;
    let itemNewDuration: number;
    let itemNewTrackIndex: number;

    if (snapshot.id === dragInfo.id) {
      // Primary item uses constrained values
      itemNewStart = Math.max(0, Math.min(totalDuration - finalDuration, snapshot.originalStart + deltaTime));
      itemNewDuration = finalDuration;
      itemNewTrackIndex = Math.max(0, Math.min(tracks.length - 1, snapshot.originalRow + constrainedDeltaTrack));
    } else {
      // Other items maintain relative position to primary using constrained deltas
      itemNewStart = Math.max(0, Math.min(totalDuration - snapshot.originalDuration, snapshot.originalStart + deltaTime));
      itemNewDuration = snapshot.originalDuration;
      itemNewTrackIndex = Math.max(0, Math.min(tracks.length - 1, snapshot.originalRow + constrainedDeltaTrack));
    }

    // Create ghost for this item
    const itemGhost = calculateGhostPosition(itemNewStart, itemNewDuration, itemNewTrackIndex, totalDuration, tracks.length);
    allGhostElements.push(itemGhost);

    // Validate each item's position
    const itemValidation = validateDropPosition(
      itemNewStart,
      itemNewDuration,
      itemNewTrackIndex,
      tracks,
      excludeIds,
      getDragInfo,
      dragInfo.action
    );

    if (!itemValidation.isValid) {
      allItemsValid = false;
    }
  }

  return { ghosts: allGhostElements, allValid: allItemsValid };
};

interface FloatingGhostParams {
  clientY: number;
  timelineRect: DOMRect;
  contentScrollTop: number;
  newDuration: number;
  totalDuration: number;
  clientX: number;
  allItemsValid: boolean;
  dragInfo: DragInfoState;
  isMultiDrag: boolean;
  tracks: TimelineTrack[];
}

export const calculateFloatingGhost = (params: FloatingGhostParams): {
  shouldShow: boolean;
  insertionIndex: number | null;
  floatingGhost: any | null;
} => {
  const {
    clientY,
    timelineRect,
    contentScrollTop,
    newDuration,
    totalDuration,
    clientX,
    allItemsValid,
    dragInfo,
    isMultiDrag,
    tracks
  } = params;

  const yWithinContent = clientY - timelineRect.top + contentScrollTop;
  const yWithinTracks = yWithinContent;
  const positionInRows = yWithinTracks / TIMELINE_CONSTANTS.TRACK_HEIGHT;
  const nearestBoundary = Math.round(positionInRows);
  const distanceToBoundary = Math.abs(positionInRows - nearestBoundary);
  const SHOW_THRESHOLD = 0.25;

  if (distanceToBoundary < SHOW_THRESHOLD && dragInfo.action === "move") {
    const ghostWidthPx = (newDuration / totalDuration) * timelineRect.width;
    const offsetX = 10;
    const offsetY = -10;

    const floatingGhost = {
      position: { x: clientX + offsetX, y: clientY + offsetY },
      width: Math.max(ghostWidthPx, 40),
      isValid: allItemsValid,
      itemData: {
        type: dragInfo.selectedItemsSnapshot[0]?.type,
        label: isMultiDrag
          ? `${dragInfo.selectedItemsSnapshot.length} items`
          : dragInfo.selectedItemsSnapshot[0]?.label,
      },
    };

    const insertionIdx = Math.max(0, Math.min(tracks.length, nearestBoundary));

    return {
      shouldShow: true,
      insertionIndex: insertionIdx,
      floatingGhost
    };
  }

  return {
    shouldShow: false,
    insertionIndex: null,
    floatingGhost: null
  };
};
